import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSnackbar } from '@/hooks/useSnackbar';
import logger from '@/logger';
import useStore from '@/store';
import { isDesktop } from '@/utils/helper';

interface ElectronFallbackContextType {
  isMediaPathFallbackToOnline: (path: string) => boolean;
  registerMediaRequest: (path: string) => void;
  unregisterMediaRequest: (path: string) => void;
}

const ElectronFallbackContext = createContext<ElectronFallbackContextType | undefined>(undefined);

export const useElectronFallback = () => {
  const context = useContext(ElectronFallbackContext);
  if (!context) {
    throw new Error('useElectronFallback must be used within an ElectronFallbackProvider');
  }
  return context;
};

interface ElectronFallbackProviderProperties {
  children: ReactNode;
}

export function ElectronFallbackProvider({ children }: ElectronFallbackProviderProperties) {
  const { desktopConfig } = useStore();
  const { showSnackbar } = useSnackbar();
  const [offlineFailedPaths, setOfflineFailedPaths] = useState<Set<string>>(new Set());
  const activeRequestingPathsReference = useRef<Set<string>>(new Set());

  const isDesktopElectron = useMemo(() => {
    return isDesktop();
  }, []);

  const handleOnlineFallback = useCallback(() => {
    const activePaths = [...activeRequestingPathsReference.current];
    if (desktopConfig?.allowOnlineStreaming && activePaths.length > 0) {
      showSnackbar('File not found locally. Streaming from online source instead.');

      // Mark ALL currently requesting paths for online fallback
      setOfflineFailedPaths((previous) => {
        const newSet = new Set(previous);
        for (const path of activePaths) newSet.add(path);
        return newSet;
      });
    } else {
      logger.info('Content not available offline');
    }
  }, [desktopConfig?.allowOnlineStreaming, showSnackbar]);

  useEffect(() => {
    if (isDesktopElectron) {
      window.electronAPI?.onTryOnlineFallback(handleOnlineFallback);
    }

    return () => {
      if (isDesktopElectron) {
        window.electronAPI?.removeOnlineFallbackListener(handleOnlineFallback);
      }
    };
  }, [isDesktopElectron, handleOnlineFallback]);

  const isMediaPathFallbackToOnline = useCallback(
    (path: string) => {
      return offlineFailedPaths.has(path);
    },
    [offlineFailedPaths],
  );

  const registerMediaRequest = useCallback((path: string) => {
    const normalizedPath = path.replace(/^\//, '');
    activeRequestingPathsReference.current.add(normalizedPath);
  }, []);

  const unregisterMediaRequest = useCallback((path: string) => {
    const normalizedPath = path.replace(/^\//, '');
    activeRequestingPathsReference.current.delete(normalizedPath);
  }, []);

  const value = useMemo(
    () => ({
      isMediaPathFallbackToOnline,
      registerMediaRequest,
      unregisterMediaRequest,
    }),
    [isMediaPathFallbackToOnline, registerMediaRequest, unregisterMediaRequest],
  );

  return (
    <ElectronFallbackContext.Provider value={value}>{children}</ElectronFallbackContext.Provider>
  );
}
